/**
 * ContactShadowsNode — TSL screen-space contact shadows.
 *
 * Algorithm (GDC / Bungie / UE5 technique):
 *
 *   For each pixel P:
 *   1. Reconstruct view-space position from depth buffer.
 *   2. Apply a surface slope bias along the view-space light direction to
 *      prevent self-shadowing acne on the receiver.
 *   3. Optionally jitter the start offset using interleaved gradient noise
 *      (IGN) — hides banding and amortises over temporal AA frames.
 *   4. March N steps along the view-space light direction, advancing by
 *      stepSize each iteration (stepSize = maxDistance / sampleCount).
 *   5. At each step:
 *       a. Project the current march position back to screen UV.
 *       b. Read the hardware depth at that UV.
 *       c. Reconstruct the view-space depth of the scene sample.
 *       d. If marchDepth > sceneDepth + thickness → in shadow → early exit.
 *   6. The result is a scalar in [0, 1]:
 *       0 = fully lit, 1 = fully shadowed.
 *      It is returned as outputNode = sceneColor * (1 - shadow * strength).
 *
 * Position reconstruction (depth → view-space):
 *   ndc.xy = uv * 2 - 1
 *   ndc.z  = depth * 2 - 1   (WebGL NDC range [-1,1]; WebGPU is [0,1])
 *   clip   = vec4(ndc, 1)
 *   view   = invProjection * clip
 *   view  /= view.w            (perspective divide)
 *
 * The pass uses THREE uniforms so light direction and all config values
 * can be updated each frame without rebuilding the node graph.
 *
 * Output:
 *   contactShadow(colorNode, depthNode, camera, config) → TSL node
 *   The returned node is the shadowed scene colour, ready to be fed
 *   to the next pass in the PostProcessingPipeline chain.
 */

import * as THREE from 'three/webgpu';
import {
  Fn,
  Loop,
  If,
  uniform,
  float,
  int,
  vec2,
  vec3,
  vec4,
  uv,
  clamp,
  mix,
  perspectiveDepthToViewZ,
} from 'three/tsl';
import type { ContactShadowsConfig } from './ContactShadowsConfig.js';
import { resolveContactShadowsConfig } from './ContactShadowsConfig.js';

// ─── Public types ────────────────────────────────────────────────────────────

/** Live uniforms exposed so callers can update them each frame. */
export interface ContactShadowsUniforms {
  /** Normalised view-space light direction (updated each frame from scene). */
  lightDirView: { value: THREE.Vector3 };
  /** Shadow strength 0–1 (from config, mutable at runtime). */
  strength: { value: number };
  /** Depth-comparison thickness in view-space metres. */
  thickness: { value: number };
  /** 1 / projectionMatrix.elements[0] — must update on FOV/resize. */
  invM11: { value: number };
  /** 1 / projectionMatrix.elements[5]. */
  invM22: { value: number };
  /** projectionMatrix.elements[0] (forward-projection x scale). */
  m11: { value: number };
  /** projectionMatrix.elements[5] (forward-projection y scale). */
  m22: { value: number };
  /** projectionMatrix.elements[10]. */
  m33: { value: number };
  /** projectionMatrix.elements[14]. */
  m43: { value: number };
  /** camera.near — drives the coordinate-system-aware depth linearisation. */
  near: { value: number };
  /** camera.far. */
  far: { value: number };
}

/** Return value of createContactShadowsNode. */
export interface ContactShadowsNodeResult {
  /**
   * TSL output node — the scene colour darkened by contact shadows.
   * Feed this as outputNode into the PostProcessingPipeline chain in place
   * of the raw scene colour node.
   */
  node: unknown;
  /** Live uniforms — update lightDirView every frame from the active DirectionalLight. */
  uniforms: ContactShadowsUniforms;
  /** Resolved configuration (defaults + user overrides). */
  config: ContactShadowsConfig;
}

// ─── Interleaved Gradient Noise (Jimenez, 2014) ──────────────────────────────
//
// IGN produces a deterministic, spatially-uniform noise pattern in [0,1].
// It is computed from integer pixel coordinates and has very good spectral
// properties for temporal AA jitter amortisation.
//
//   ign(x, y) = fract(52.9829189 * fract(0.06711056 * x + 0.00583715 * y))
//
// We replicate this in TSL using float arithmetic (pixel coords via uv * resolution).
// The exact integer pixel coordinate is unavailable in pure TSL post-passes, so we
// approximate with screenUV * a large constant — giving good pseudo-random offset.

// ─── Depth reconstruction helpers ───────────────────────────────────────────
//
// Given:
//   uv      : screen UV in [0,1]
//   rawDepth: depth value from the depth texture (WebGL: [0,1] hardware depth)
//   invProj : camera inverse projection matrix
//
// NDC position (WebGL convention, z in [-1,1]):
//   ndc = (uv * 2 - 1, rawDepth * 2 - 1)
//
// Clip-space position (w=1 before perspective divide):
//   clip = invProj * vec4(ndc, 1.0)
//   viewPos = clip.xyz / clip.w
//
// viewPos.z is NEGATIVE in Three.js view space (right-handed, camera looks -Z).
// Depth increases as abs(viewPos.z) increases.
// The depth buffer stores projected Z, which is monotonically related to -viewPos.z.
// We compare marchPos.z against scenePos.z: both are view-space z (negative).
// marchPos is DEEPER (further from camera) when marchPos.z < scenePos.z.

// ─── createContactShadowsNode ────────────────────────────────────────────────

/**
 * Creates the screen-space contact shadows TSL node.
 *
 * @param colorNode  Scene colour texture node from `scenePass.getTextureNode('output')`.
 * @param depthNode  Scene depth texture node from `scenePass.getTextureNode('depth')`.
 * @param camera     The active camera (used for inverse projection).
 * @param partial    Optional config overrides.
 */
export function createContactShadowsNode(
  colorNode: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  partial?: Partial<ContactShadowsConfig>,
): ContactShadowsNodeResult {
  const cfg = resolveContactShadowsConfig(partial);

  // ── Live uniforms ──────────────────────────────────────────────────────────
  // Initialise light direction pointing TOWARD the light (straight up for an
  // overhead sun) — the march below advances along lightDirView toward the
  // occluders that sit between the receiver and the light. (Updated each frame.)
  const uLightDirView = uniform(new THREE.Vector3(0, 1, 0));
  const uStrength     = uniform(cfg.strength);
  const uThickness    = uniform(cfg.thickness);
  // Projection-matrix coefficients used for view-space depth reconstruction.
  // These MUST be live uniforms (not baked float() constants) — otherwise
  // any camera resize, FOV change, or projection update silently breaks
  // contact shadows because the shader keeps reconstructing depth using
  // the matrix that was active at node-construction time.
  const _initPm = (camera as THREE.PerspectiveCamera).projectionMatrix.elements;
  const uInvM11 = uniform(1.0 / (_initPm[0] === 0 ? 1e-6 : _initPm[0]));
  const uInvM22 = uniform(1.0 / (_initPm[5] === 0 ? 1e-6 : _initPm[5]));
  const uM11    = uniform(_initPm[0]);
  const uM22    = uniform(_initPm[5]);
  const uM33    = uniform(_initPm[10]);
  const uM43    = uniform(_initPm[14]);
  const _camNF = camera as { near?: number; far?: number };
  const uNear   = uniform(typeof _camNF.near === 'number' ? _camNF.near : 0.1);
  const uFar    = uniform(typeof _camNF.far === 'number' ? _camNF.far : 1000);

  // Baked constants (not uniforms — baked into shader at compile time for perf).
  const stepSize = cfg.maxDistance / cfg.sampleCount;       // metres per step

  // We need the camera inverse projection matrix as a uniform so the shader can
  // reconstruct view-space positions from depth + UV.
  // THREE.js provides cameraProjectionMatrixInverse as a built-in TSL node.
  // We import it from three/tsl — it auto-updates with the camera each frame.

  // ── TSL kernel ─────────────────────────────────────────────────────────────
  const contactShadowFn = Fn((): any => {
    // ── 1. Current pixel UV ──────────────────────────────────────────────────
    const pixelUV = uv().toVar();

    // ── 2. Sample depth at the receiver pixel ────────────────────────────────
    // depthNode is the scene depth texture from the pass node.
    // We sample it at the current pixel's UV.
    const rawDepth = ((depthNode as any).sample
      ? (depthNode as any).sample(pixelUV)
      : (depthNode as any).uv
        ? (depthNode as any).uv(pixelUV)
        : (depthNode as any)
    ).toVar();

    // Skip far pixels: return full-lit (shadow = 0), applied as the lerp
    // shadow = shadow * (1 - isSky).
    //
    // NOTE (2026-09-14): 0.9999 on RAW depth is NOT the far plane — hardware
    // depth is non-linear, so with a 0.05 m near plane this is z ≈ 500 m (see
    // render/depth/skyMask.ts for the arithmetic and the three passes where
    // the same assumption WAS a bug). Here it is harmless and deliberate:
    // contact shadows are a short-range grounding effect, so a 500 m cutoff
    // costs nothing visually and skips the raymarch on distant terrain.
    // Do NOT "fix" this to skyMask() — that would re-enable the march for
    // every far pixel for no visible gain.
    const isSky = (rawDepth as any).greaterThanEqual(float(0.9999)).toVar();

    // ── 3. Reconstruct view-space position of the receiver ───────────────────
    // NDC xy from UV: ndc = uv * 2 - 1
    const ndcX = (pixelUV as any).x.mul(float(2)).sub(float(1)).toVar();
    const ndcY = (pixelUV as any).y.mul(float(2)).sub(float(1)).toVar();
    // NDC z (WebGL [-1,1] convention): rawDepth * 2 - 1
    const ndcZ = (rawDepth as any).mul(float(2)).sub(float(1)).toVar();

    // Reconstruct view-space position via inverse projection.
    // THREE.js TSL provides cameraProjectionMatrixInverse as a built-in node.
    // We import it dynamically to avoid a circular dependency at the type level.
    // Resolve at runtime via (THREE as any).
    // We use a manual reconstruction with the camera's projection matrix here,
    // feeding it as a uniform so we don't depend on TSL built-ins that may not
    // be available in all Three.js r182 builds.
    //
    // For a perspective camera (standard):
    //   projMatrix = camera.projectionMatrix
    //   invProj    = camera.projectionMatrixInverse
    //   clip = invProj * vec4(ndc, 1)
    //   view = clip.xyz / clip.w
    //
    // (An unused Matrix4 uniform used to be allocated here on every shader
    // build; the reconstruction below reads the scalar coefficient uniforms.)

    // Reconstruct:  clip = invProj * vec4(ndcX, ndcY, ndcZ, 1)
    // For a standard perspective matrix the formulas reduce to:
    //   viewX = ndcX * (-1 / proj[0])     (proj[0] = 2*near/(right-left))
    //   viewY = ndcY * (-1 / proj[5])     (proj[5] = 2*near/(top-bottom))
    //   viewZ = ... (not trivially separable, use full matrix)
    //
    // We perform the full 4x4 multiply via TSL vec4/mat4 ops.
    // Three/tsl exposes `mat4` and `vec4` — we compose them manually.
    //
    // Note: in practice the actual inverse-projection transform is done by
    // leveraging cameraProjectionMatrixInverse built-in, but since we can't
    // depend on TSL internal built-in availability across Three r182 patch
    // versions, we fall back to a uniform-based approach.
    //
    // Simplified: use the camera's projectionMatrixInverse to transform NDC → view.
    // The TSL code below performs: viewPos = invProj * vec4(ndcX, ndcY, ndcZ, 1)
    // and then perspective-divides.
    //
    // Because TSL does not expose matrix * vector natively in a simple portable
    // form without importing from addons, we use the algebraic expansion:
    //
    //   For a standard symmetric perspective proj matrix M:
    //     M^-1[0][0] = 1/M[0][0]      (x scale)
    //     M^-1[1][1] = 1/M[1][1]      (y scale)
    //   viewX = ndcX / projMatrix.elements[0]
    //   viewY = ndcY / projMatrix.elements[5]
    //   viewZ = -1 (in view space, at the near/far plane, z is reconstructed below)
    //
    // Full z reconstruction from NDC z for standard perspective:
    //   proj[10] = -(far+near)/(far-near),  proj[14] = -2*far*near/(far-near)
    //   viewZ    = proj[14] / (ndcZ + proj[10])     (rearranging standard formula)
    //
    // We extract these from the camera's projectionMatrix.elements.
    // Projection-matrix coefficients live in uniforms (declared above) so
    // they track camera resize / FOV updates each frame. ContactShadowsSystem
    // is responsible for refreshing them in execute().
    const invM11 = uInvM11;
    const invM22 = uInvM22;
    const m33    = uM33;
    const m43    = uM43;

    // View Z (negative in front of the camera). The old hand-rolled
    // `m43 / (ndcZ + m33)` was sign-inverted (three's inverse is −m43/(ndc+m33))
    // AND assumed WebGL's [-1,1] NDC-Z on a WebGPU-first engine — the march
    // clamped every step to z = −1e-6 and the pass never darkened a pixel.
    // perspectiveDepthToViewZ is coordinate-system- and reversed-Z-aware.
    void ndcZ;
    const viewZ = (perspectiveDepthToViewZ(rawDepth as any, uNear as any, uFar as any) as any).toVar();

    // View X, Y via linear scale: viewX = ndcX * viewZ * invM11 / (-1)
    // Derivation: x_ndc = x_view * proj[0] / (-viewZ)  → x_view = x_ndc * (-viewZ) / proj[0]
    const viewX = (ndcX as any).mul((viewZ as any).negate()).mul(invM11).toVar();
    const viewY = (ndcY as any).mul((viewZ as any).negate()).mul(invM22).toVar();

    // View-space receiver position
    const receiverPos = vec3(viewX, viewY, viewZ).toVar();

    // ── 4. Compute step vector in view space ─────────────────────────────────
    const stepSizeNode = float(stepSize);
    // Light direction in view space (uniform, updated each frame by ContactShadowsSystem).
    const lightDir = vec3(
      (uLightDirView as any).x,
      (uLightDirView as any).y,
      (uLightDirView as any).z,
    ).toVar();
    // Step: advance TOWARD the light source (reverse of shadow ray = toward light)
    // We ray-march from the receiver toward the light, checking if geometry occludes.
    const stepVec = (lightDir as any).mul(stepSizeNode).toVar();

    // ── 5. Jitter the start offset (interleaved gradient noise) ───────────────
    // IGN(x,y) = fract(52.9829189 * fract(0.06711056*x + 0.00583715*y))
    // x,y are pixel coordinates — approximate via UV * large constant.
    let startOffset = float(0).toVar();
    if (cfg.noise) {
      const ux = (pixelUV as any).x.mul(float(1920));  // approx pixel X at 1080p
      const uy = (pixelUV as any).y.mul(float(1080));  // approx pixel Y at 1080p
      const inner = (ux as any).mul(float(0.06711056)).add((uy as any).mul(float(0.00583715)));
      // fract via sub floor: x - floor(x)
      // TSL has .fract() as a method on float nodes
      const innerFract = (inner as any).fract ? (inner as any).fract() : inner;
      const outer = (innerFract as any).mul(float(52.9829189));
      const noise = (outer as any).fract ? (outer as any).fract() : outer;
      // Scale noise to [0, stepSize] so the jitter is sub-step
      startOffset = (noise as any).mul(stepSizeNode).toVar();
    }

    // Initial march position: receiver + slope bias + noise offset.
    // The slope bias (one step forward along the light) prevents the receiver
    // from self-shadowing due to limited depth precision at grazing angles.
    const slopeBiasNode = float(stepSize * cfg.slopeBias);
    const marchPos = ((receiverPos as any).add(
      (lightDir as any).mul((slopeBiasNode as any).add(startOffset)),
    )).toVar();

    // ── 6. Shadow accumulator ────────────────────────────────────────────────
    // 0.0 = lit, 1.0 = shadowed. We break on first hit (hard contact shadow).
    const shadowFactor = float(0.0).toVar();

    // Sample count as integer for Loop
    Loop(int(cfg.sampleCount), (): any => {
      // Advance march position along the light direction
      (marchPos as any).addAssign(stepVec);

      // ── Project marchPos to screen space ──────────────────────────────────
      // Forward projection (view → NDC):
      //   x_ndc = x_view * proj[0] / (-z_view)
      //   y_ndc = y_view * proj[5] / (-z_view)
      //   depth_ndc = (z_view * proj[10] + proj[14]) / (-z_view)
      const negZ = (marchPos as any).z.negate();
      // Guard against z = 0 (shouldn't happen for valid geometry, but defensive)
      const safeNegZ = (negZ as any).max(float(1e-6));
      // Forward-projection coefficients: same uniforms refreshed each frame
      // by ContactShadowsSystem (see uM11/uM22 declarations above).
      const m11 = uM11;
      const m22 = uM22;
      const marchNdcX = (marchPos as any).x.mul(m11).div(safeNegZ).toVar();
      const marchNdcY = (marchPos as any).y.mul(m22).div(safeNegZ).toVar();
      const marchNdcZ = ((marchPos as any).z.mul(m33).add(m43)).div(safeNegZ).toVar();

      // NDC → UV: uv = ndc * 0.5 + 0.5
      const marchUV = vec2(
        (marchNdcX as any).mul(float(0.5)).add(float(0.5)),
        (marchNdcY as any).mul(float(0.5)).add(float(0.5)),
      ).toVar();

      // Skip samples outside the screen
      const inScreen = (marchUV as any).x.greaterThanEqual(float(0.0))
        .and((marchUV as any).x.lessThanEqual(float(1.0)))
        .and((marchUV as any).y.greaterThanEqual(float(0.0)))
        .and((marchUV as any).y.lessThanEqual(float(1.0)));

      If(inScreen, (): any => {
        // Clamp UV for safe sampling
        const clampedUV = clamp(marchUV as any, float(0.0), float(1.0));

        // Sample scene depth at march UV
        const sampledDepth = ((depthNode as any).sample
          ? (depthNode as any).sample(clampedUV)
          : (depthNode as any).uv
            ? (depthNode as any).uv(clampedUV)
            : (depthNode as any)
        ).toVar();

        // Skip sky/background samples
        const isSkyStep = (sampledDepth as any).greaterThanEqual(float(0.9999));

        If((isSkyStep as any).not(), (): any => {
          // Reconstruct scene view-space Z at sampled position.
          // Convert hardware depth [0,1] → NDC z [-1,1] → view Z.
          const sceneViewZ = (perspectiveDepthToViewZ(sampledDepth as any, uNear as any, uFar as any) as any).toVar();

          // March view Z. WHY marchPos.z: the previous round-trip
          // `m43 / (marchNdcZ + m33)` was the NEGATED inverse of the forward
          // projection above (z = −m43/(ndc+m33)), so marchViewZ came out
          // POSITIVE while sceneViewZ is negative — zDiff was always < 0,
          // `isBehind` never fired and the pass darkened zero pixels (the
          // receiver-side fix at step 3 never reached the march). The march
          // position already IS view space; its z needs no reconstruction.
          void marchNdcZ;
          const marchViewZ = (marchPos as any).z.toVar();

          // Occlusion test:
          //   marchViewZ > sceneViewZ → march position is BEHIND scene geometry
          //   (more negative = further from camera in right-handed view space)
          //   In Three.js view space: z is negative (camera looks -Z).
          //   Closer to camera = larger z (less negative).
          //   Behind geometry means: marchViewZ < sceneViewZ (more negative).
          //
          //   Shadow condition: sceneViewZ - marchViewZ < thickness
          //     i.e., scene is between camera and march position by < thickness
          //   This avoids shadows leaking through thick geometry.
          const zDiff = (sceneViewZ as any).sub(marchViewZ).toVar();
          // In shadow if: zDiff > 0 (march is behind scene) AND zDiff < thickness
          const uThicknessLocal = uThickness;
          const isBehind = (zDiff as any).greaterThan(float(0.0));
          const isWithinThickness = (zDiff as any).lessThan(uThicknessLocal);

          If((isBehind as any).and(isWithinThickness), (): any => {
            // Fade the shadow based on how far along the march we are.
            // marchNdcZ in [-1,1]; convert to fraction of maxDistance [0,1].
            // Simple falloff: shadow is full at the receiver, fades to 0 at maxDistance.
            // We use the step count ratio rather than the z because step count is stable.
            // For now: hard shadow (factor = 1) — temporal AA will soften it.
            (shadowFactor as any).assign(float(1.0));
          });
        });
      });
    });

    // ── 7. Sky mask: no shadow on background pixels ──────────────────────────
    // If receiver is sky (isSky), shadow = 0.
    const finalShadow = mix(shadowFactor as any, float(0.0), isSky as any).toVar();

    // ── 8. Apply shadow to scene colour: darken by (1 - shadow * strength) ───
    const darken = (finalShadow as any).mul(uStrength).toVar();
    const shadowMultiplier = float(1.0).sub(darken as any).toVar();

    // Sample scene colour at current pixel UV
    const sceneColor = ((colorNode as any).sample
      ? (colorNode as any).sample(pixelUV)
      : (colorNode as any).uv
        ? (colorNode as any).uv(pixelUV)
        : (colorNode as any)
    ).toVar();

    // Multiply RGB by shadow factor, preserve alpha
    return vec4(
      (sceneColor as any).rgb.mul(shadowMultiplier as any),
      (sceneColor as any).a,
    );
  });

  const node = contactShadowFn();

  // ── Return ──────────────────────────────────────────────────────────────────
  const uniforms: ContactShadowsUniforms = {
    lightDirView: uLightDirView as any,
    strength:     uStrength as any,
    thickness:    uThickness as any,
    invM11:       uInvM11 as any,
    invM22:       uInvM22 as any,
    m11:          uM11 as any,
    m22:          uM22 as any,
    m33:          uM33 as any,
    m43:          uM43 as any,
    near:         uNear as any,
    far:          uFar as any,
  };

  return { node, uniforms, config: cfg };
}

// ─── Utility: extract primary DirectionalLight direction into view space ──────

/** Scratch objects (zero-alloc per frame). */
const _lightDir   = new THREE.Vector3();
// Per-scene primary-light cache (see getPrimaryLightDirView). WeakMap so a
// discarded scene never pins its light.
const _dirLightCache = new WeakMap<THREE.Scene, THREE.DirectionalLight>();
const _viewMatrix = new THREE.Matrix4();

/**
 * Finds the first DirectionalLight in the scene hierarchy and returns its
 * direction in view space, normalised.
 *
 * Falls back to straight-down (-Y in view space) if no DirectionalLight is found.
 *
 * Call this once per frame and assign the result to:
 *   contactShadowsUniforms.lightDirView.value.copy(dir)
 *
 * @param scene   THREE.Scene (or any Object3D root).
 * @param camera  Active camera — used to transform from world → view space.
 */
export function getPrimaryLightDirView(
  scene: THREE.Scene,
  camera: THREE.Camera,
): THREE.Vector3 {
  // Cache the found light per scene: this runs every frame and traverse()
  // visits EVERY node even after a hit (the early return only skips the
  // callback body). Re-traverse only when the cached light left the scene —
  // an O(parent-depth) walk instead of an O(scene) one.
  let dirLight: THREE.DirectionalLight | null = _dirLightCache.get(scene) ?? null;
  if (dirLight) {
    let p: THREE.Object3D | null = dirLight;
    while (p && p !== scene) p = p.parent;
    if (p !== scene) dirLight = null; // detached — fall through to re-scan
  }
  if (!dirLight) {
    scene.traverse((obj) => {
      if (dirLight) return;
      if (obj instanceof THREE.DirectionalLight) {
        dirLight = obj;
      }
    });
    if (dirLight) _dirLightCache.set(scene, dirLight);
    else _dirLightCache.delete(scene);
  }

  if (!dirLight) {
    // Default: light from above. The march expects a direction pointing TOWARD
    // the light, so an overhead light → (0,+1,0) in view space for an identity
    // camera (NOT the direction the rays travel).
    _lightDir.set(0, 1, 0);
    return _lightDir;
  }

  // Direction TOWARD the light (light.position - target.position). The march in
  // sampleVsm/contactShadowFn steps along lightDirView from the receiver toward
  // the light to find occluders, so it needs the toward-light direction — NOT
  // `target - position` which is the direction the light RAYS travel (away from
  // the source). setLightDirectionWorld() uses the same toward-light convention.
  const light = dirLight as THREE.DirectionalLight;
  _lightDir.subVectors(light.position, light.target.position).normalize();

  // Transform world → view space using camera's view matrix
  _viewMatrix.copy(camera.matrixWorldInverse);
  _lightDir.transformDirection(_viewMatrix);

  return _lightDir;
}
